import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus, Upload, X } from "lucide-react";
import { Loader } from "@/components/shared/loader";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import FileUploadField from "@/components/shared/form-fields/file-upload-field";
import { InputTypes } from "@/constants/enums";
import { isAllowedDocumentFile } from "@/lib/upload-filename";
import { useUploadDocument } from "@/features/documents/hooks/useDocumentsMutations";
import {
  useCreateDocumentLibrary,
  useDocumentLibraries,
} from "@/features/documents/hooks/useDocumentLibrariesQueries";
import type { DepartmentOption } from "./DocumentsFilters";

const uploadSchema = z.object({
  file: z
    .custom<File>((val) => val instanceof File, { message: "يرجى اختيار ملف" })
    .refine((file) => file.size > 0, { message: "يرجى اختيار ملف" })
    .refine((file) => isAllowedDocumentFile(file), {
      message: "نوع الملف غير مدعوم. يُسمح بملفات PDF وWord وExcel فقط.",
    }),
  name: z.string().trim().min(1, "الاسم مطلوب"),
  departmentId: z.string().optional(),
  libraryId: z.string().optional(),
  // 🔴 "none" IS THE DEFAULT, AND `ku` IS NOT AN OPTION.
  // "none" is submitted as NO language at all, which the backend stores as NULL = UNLABELLED, and
  // publicDocuments serves an unlabelled document on BOTH locales. Defaulting to "ar" is what made
  // every uploaded document Arabic-only and dropped it from /en. An uploader who does not choose is
  // not asserting Arabic — only label a document that genuinely belongs to one locale.
  // `ku` was never a valid value: the backend enum is ["ar","en"], so picking it failed validation.
  language: z.enum(["none", "ar", "en"]),
});

type UploadFormValues = z.infer<typeof uploadSchema>;

const NONE = "NONE";

interface UploadDocumentDialogProps {
  departments: DepartmentOption[];
  /** When set, department is locked (Editors). */
  lockedDepartmentId?: string;
  /** Whether the user may create new libraries inline. */
  canCreateLibrary?: boolean;
}

export function UploadDocumentDialog({
  departments,
  lockedDepartmentId,
  canCreateLibrary = true,
}: UploadDocumentDialogProps) {
  const [open, setOpen] = useState(false);
  const [creatingLibrary, setCreatingLibrary] = useState(false);
  const [newLibraryTitle, setNewLibraryTitle] = useState("");
  const [newLibraryDepartmentId, setNewLibraryDepartmentId] = useState("");
  const uploadMutation = useUploadDocument();
  const createLibrary = useCreateDocumentLibrary();

  const formMethods = useForm<UploadFormValues>({
    defaultValues: {
      file: undefined,
      name: "",
      departmentId: lockedDepartmentId ?? "",
      libraryId: "",
      language: "none",
    },
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: zodResolver(uploadSchema),
  });

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    getValues,
    reset,
    formState: { errors, isSubmitting },
  } = formMethods;

  // Default the display name to the picked file's base name (only while still empty).
  const selectedFile = watch("file") as File | undefined;
  useEffect(() => {
    if (selectedFile instanceof File && !getValues("name")) {
      setValue("name", selectedFile.name.replace(/\.[^/.]+$/, ""), {
        shouldValidate: true,
      });
    }
  }, [selectedFile, getValues, setValue]);

  // Libraries are scoped to the selected department ("divided and organised by
  // department"). Reset the chosen library when the department changes so a stale
  // cross-department library can't be submitted.
  const selectedDepartment = watch("departmentId");
  const { data: librariesData } = useDocumentLibraries(
    selectedDepartment ? { departmentId: selectedDepartment } : undefined,
  );
  const libraries = useMemo(
    () => librariesData?.items ?? [],
    [librariesData],
  );
  useEffect(() => {
    setValue("libraryId", "");
    setCreatingLibrary(false);
    setNewLibraryTitle("");
    setNewLibraryDepartmentId("");
  }, [selectedDepartment, setValue]);

  function openCreateLibrary() {
    setCreatingLibrary(true);
    setNewLibraryTitle("");
    setNewLibraryDepartmentId(lockedDepartmentId ?? selectedDepartment ?? "");
  }

  function closeCreateLibrary() {
    setCreatingLibrary(false);
    setNewLibraryTitle("");
    setNewLibraryDepartmentId("");
  }

  const isPending = isSubmitting || uploadMutation.isPending;

  function resetForm() {
    reset({
      file: undefined,
      name: "",
      departmentId: lockedDepartmentId ?? "",
      libraryId: "",
      language: "none",
    });
    setCreatingLibrary(false);
    setNewLibraryTitle("");
    setNewLibraryDepartmentId("");
  }

  async function onCreateLibrary() {
    const title = newLibraryTitle.trim();
    if (!title) return;
    try {
      const created = await createLibrary.mutateAsync({
        title,
        ...(newLibraryDepartmentId ? { departmentId: newLibraryDepartmentId } : {}),
      });
      setValue("libraryId", created.id, { shouldValidate: true });
      if (!selectedDepartment && newLibraryDepartmentId) {
        setValue("departmentId", newLibraryDepartmentId, { shouldValidate: true });
      }
      closeCreateLibrary();
    } catch {
      /* mutation surfaces the error via onError */
    }
  }

  async function onSubmit(data: UploadFormValues) {
    try {
      await uploadMutation.mutateAsync({
        file: data.file,
        departmentId: data.departmentId ?? "",
        name: data.name.trim(),
        libraryId: data.libraryId || undefined,
        // 🔴 "none" MUST BE SENT AS NOTHING, NOT AS THE STRING "none".
        // documentService.upload does `if (language) formData.append(...)`, so `undefined` omits
        // the field entirely and the backend stores NULL (unlabelled → served on both locales).
        // Sending "none" would fail the backend enum ["ar","en"].
        language: data.language === "none" ? undefined : data.language,
      });
      resetForm();
      setOpen(false);
    } catch {
      /* mutation surfaces the error via onError */
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (isPending) return;
        setOpen(next);
        if (!next) resetForm();
      }}
    >
      <DialogTrigger asChild>
        <Button variant="default">
          <Upload className="w-4 h-4 ml-2" />
          رفع مستند
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader className="text-right">
          <DialogTitle>رفع مستند جديد</DialogTitle>
          <DialogDescription>
            اختر ملفاً وحدد اسمه والدائرة التابع لها.
          </DialogDescription>
        </DialogHeader>

        <Form {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <FileUploadField
              name="file"
              label="الملف"
              type={InputTypes.FILE}
              control={control}
              errors={errors}
              accept=".pdf,.doc,.docx,.xls,.xlsx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              allowedTypes={[
                "application/pdf",
                "application/msword",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                "application/vnd.ms-excel",
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              ]}
              maxSize={50}
            />

            <FormField
              control={control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>اسم المستند</FormLabel>
                  <FormControl>
                    <Input placeholder="أدخل اسم المستند" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="language"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>اللغة</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="اختر اللغة" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">
                        بدون تحديد — يظهر في اللغتين
                      </SelectItem>
                      <SelectItem value="ar">العربية فقط</SelectItem>
                      <SelectItem value="en">English only</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="departmentId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>الدائرة</FormLabel>
                  <Select
                    value={field.value || NONE}
                    onValueChange={(v) => field.onChange(v === NONE ? "" : v)}
                    disabled={!!lockedDepartmentId}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="اختر الدائرة" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={NONE}>بدون دائرة</SelectItem>
                      {departments.map((option) => (
                        <SelectItem key={option.id} value={option.id}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="libraryId"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between">
                    <FormLabel>المكتبة (الموقع)</FormLabel>
                    {canCreateLibrary ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80"
                      onClick={() =>
                        creatingLibrary ? closeCreateLibrary() : openCreateLibrary()
                      }
                    >
                      {creatingLibrary ? (
                        <>
                          <X className="w-3 h-3" /> إلغاء
                        </>
                      ) : (
                        <>
                          <Plus className="w-3 h-3" /> مكتبة جديدة
                        </>
                      )}
                    </button>
                    ) : null}
                  </div>

                  {creatingLibrary ? (
                    <div className="space-y-3 rounded-md border border-border p-3">
                      <div className="space-y-1">
                        <FormLabel>اسم المكتبة</FormLabel>
                        <Input
                          placeholder="مثال: التقارير السنوية"
                          value={newLibraryTitle}
                          onChange={(e) => setNewLibraryTitle(e.target.value)}
                        />
                      </div>
                      <div className="space-y-1">
                        <FormLabel>الدائرة</FormLabel>
                        <Select
                          value={newLibraryDepartmentId || NONE}
                          onValueChange={(v) =>
                            setNewLibraryDepartmentId(v === NONE ? "" : v)
                          }
                          disabled={!!lockedDepartmentId}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="اختر الدائرة" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NONE}>بدون دائرة</SelectItem>
                            {departments.map((option) => (
                              <SelectItem key={option.id} value={option.id}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        disabled={
                          !newLibraryTitle.trim() || createLibrary.isPending
                        }
                        onClick={onCreateLibrary}
                      >
                        إنشاء المكتبة
                        {createLibrary.isPending && <Loader className="mr-2" />}
                      </Button>
                    </div>
                  ) : (
                    <Select
                      value={field.value || NONE}
                      onValueChange={(v) =>
                        field.onChange(v === NONE ? "" : v)
                      }
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="اختر المكتبة" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={NONE}>بدون مكتبة</SelectItem>
                        {libraries.map((option) => (
                          <SelectItem key={option.id} value={option.id}>
                            {option.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" disabled={isPending} className="w-full">
              رفع المستند
              {isPending && <Loader className="mr-2" />}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
